import React, { useContext } from "react";
import { Box, makeStyles } from "@material-ui/core";
import { tesaBlue, tesaDarkRedError } from "../../../../../../styles/colors";
import { TestSeriesContext } from "../../../../../../contexts/TestSeriesContext";
import ActionValue from "../../../../../../api/models/test-series/ActionValue";
import ParameterValue from "../../../../../../api/models/test-series/ParameterValue";
import CustomDynamicContextMenu from "../../../../../../components/Menu/CustomDynamicContextMenu";
import Action from "../../../../../../api/models/test-series/Action";
import TesaTmbApi, { mapAction, mapParameter } from "../../../../../../api/services/TesaTmbApi";
import TmbAction from "../../../../../../api/models/tmb/TmbAction";
import Parameter from "../../../../../../api/models/test-series/Parameter";
import TmbParameter from "../../../../../../api/models/tmb/TmbParameter";
import ActionValueValidator from "../../../../../../validator/ActionValueValidator";
import ParameterValueValidator, { toParameterValue } from "../../../../../../validator/ParameterValueValidator";
import NumberParameterValue from "../../../../../../api/models/test-series/NumberParameterValue";
import EnumParameterValue from "../../../../../../api/models/test-series/EnumParameterValue";
import ParameterValueGroup from "../../../../../../api/models/test-series/ParameterValueGroup";
import WhiteChipTooltip from "../../../../../../components/Chip/WhiteChipTooltip";
import extractValueAndUnitCode from "../../../../../../helper/ParameterValueExtractor";
// eslint-disable-next-line max-len
import ConfirmationDialogContext from "../../../../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import ActionTemplate from "../../../../../../api/models/test-series/ActionTemplate";
import VerticalAlignedChip from "../../../../../../components/Chip/VerticalAlignedChip";

export interface TestSeriesWhiteChipTableElementProps {
  conditions?: ParameterValueGroup[];
  chips: (ParameterValue | ActionValue)[];
  updateParentState: (chips: (ParameterValue | ActionValue)[]) => void;
  section: "substrate" | "material" | "testpiece" | "condition" | "measurement";
  flowType?: "TREATMENT" | "PREPARATION";
  enabled: boolean;
  validated: boolean;
  parametersInAllTests?: ParameterValue[];
}

const useStyles = makeStyles({
  root: {
    width: "100%",
    maxWidth: 360,
  },
  tableElement: {
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "0px",
  },
  highlightedTableElement: {
    color: tesaBlue,
    borderColor: tesaBlue,
    borderStyle: "dashed",
    deleteIconColor: tesaBlue,
  },
  highlightedTableElementError: {
    color: tesaDarkRedError,
    borderColor: tesaDarkRedError,
    borderStyle: "dashed",
    deleteIconColor: tesaDarkRedError,
  },
  noPadding: {
    paddingTop: "0px",
    paddingBottom: "0px",
  },
  listAddButton: {
    color: "#757575",
    fontSize: "0.85rem",
    padding: 0,
    paddingTop: "10px",
    paddingBottom: "10px",
    size: "small",
    minWidth: "20px !important",
    maxWidth: "20px !important",
  },
});

const TestSeriesWhiteChipTableElement: React.FC<TestSeriesWhiteChipTableElementProps> = (
  props: TestSeriesWhiteChipTableElementProps,
) => {
  const classes = useStyles(props);
  const { chips, conditions, updateParentState, section, flowType, enabled, validated, parametersInAllTests } = props;
  const { permissions, showParameterListDialog } = useContext(TestSeriesContext);
  const dialogContext = useContext(ConfirmationDialogContext);
  const tesaTmbApi: TesaTmbApi = new TesaTmbApi(dialogContext);

  const hasUncontrolledParameters = section !== "measurement" && section !== "condition";

  const addChip = (chip: ActionValue | ParameterValue): void => {
    const index = chips.findIndex((value) => (value as { parameter?: Parameter }).parameter?.tmbId === 99999);
    if (index >= 0) {
      chips.splice(index, 0, chip);
    } else {
      chips.push(chip);
    }
    updateParentState(chips);
  };

  const updateUnit = (paramVals: ParameterValue[], unitCode: string | null) => {
    paramVals.forEach((pVal) => {
      const idx = (chips as ParameterValue[]).findIndex((chip) => {
        return chip.parameter.tmbId === pVal.parameter.tmbId;
      });
      if (idx >= 0) {
        chips.splice(idx, 1, { ...pVal, unitCode } as NumberParameterValue);
      }
    });
  };

  const deleteChip = (i: number): void => {
    chips.splice(i, 1);
    updateParentState(chips);
  };

  const fetchActionTemplates = (actionTmbId: number): Promise<ActionTemplate[]> =>
    tesaTmbApi.fetchActionTemplates(actionTmbId);

  const showActionDialog = (e: ActionValue): void => {
    const markedParamValues: ParameterValue[] = e.parameterValues.map((pVal) => {
      return { ...pVal, action: true };
    });

    showParameterListDialog(
      e.action.name,
      e.action.parameters,
      markedParamValues,
      hasUncontrolledParameters &&
        // prevent rendering includeLabel when only table actions present
        (!!e.action.parameters?.length || !!markedParamValues?.length),
      {
        disabled: !enabled,
        fetchActionTemplates: () => fetchActionTemplates(e.action.tmbId),
        rows: e.rows,
        rowParameters: e.action.rowParameters,
      },
    )
      .then(({ values, rows }) => {
        e.parameterValues = values;
        e.rows = rows || [];
        updateParentState(chips);
      })
      .catch(() => {
        // do nothing on close
      });
  };

  const showParameterValueDialog = (allParameterValues: ParameterValue[], parameterValue: ParameterValue): void => {
    showParameterListDialog(
      parameterValue.parameter.name,
      [parameterValue.parameter],
      [parameterValue],
      hasUncontrolledParameters,
      {
        disabled: !enabled || (parameterValue.parameter.tmbId === 99999 && !permissions.editRepetitions),
      },
    )
      .then(({ values }) => {
        allParameterValues.splice(allParameterValues.indexOf(parameterValue), 1, values[0]);
        updateParentState(chips);
      })
      .catch(() => {
        // do nothing on close
      });
  };

  const showNumberParameterDialog = (all: ParameterValue[], e: NumberParameterValue): void => {
    const enumValue: EnumParameterValue = {
      type: "ENUM",
      value: e.unitCode,
      parameter: {
        tmbId: e.parameter.tmbId,
        values: e.parameter.unit.scalings.map((s) => s.code),
        name: e.parameter.name,
        type: "ENUM",
      },
    };
    showParameterListDialog(e.parameter.name, [enumValue.parameter], [enumValue], hasUncontrolledParameters, {
      disabled: !enabled,
      unitType: e.parameter.unit.type,
    })
      .then(({ values }) => {
        const enumResultValue = values[0] as EnumParameterValue;
        const unitCode = enumResultValue.value;
        all.splice(all.indexOf(e), 1, {
          ...e,
          unitCode,
        } as NumberParameterValue);
        if (values.length >= 1) {
          updateUnit(values.slice(1), unitCode);
        }
        updateParentState(chips);
      })
      .catch(() => {
        // do nothing on close
      });
  };

  const chipClassName = (valid: boolean): string | undefined => {
    if (valid) {
      return undefined;
    }
    return validated ? classes.highlightedTableElementError : classes.highlightedTableElement;
  };

  const renderActionValue = (actionValue: ActionValue, index: number): JSX.Element => {
    return (
      <WhiteChipTooltip
        header={actionValue.action.name}
        values={actionValue.parameterValues
          .filter((parameterValue) => !parameterValue.uncontrolled)
          .map((parameterValue) => {
            const valueAndUnitCode = extractValueAndUnitCode(parameterValue as ParameterValue);

            return {
              label: parameterValue.parameter.name,
              value: valueAndUnitCode.value,
              unitCode: valueAndUnitCode.unitCode,
            };
          })}
      >
        <VerticalAlignedChip
          label={actionValue.action.name}
          onDelete={
            enabled
              ? (): void => {
                  deleteChip(index);
                }
              : undefined
          }
          variant="outlined"
          onClick={(): void => {
            showActionDialog(actionValue);
          }}
          className={chipClassName(ActionValueValidator.validate(actionValue).valid)}
        />
      </WhiteChipTooltip>
    );
  };

  const renderParameterValue = (parameterValue: ParameterValue, index: number): JSX.Element => {
    const valueAndUnitCode = extractValueAndUnitCode(parameterValue);
    return (
      <WhiteChipTooltip
        header={parameterValue.parameter.name}
        values={[
          {
            value: valueAndUnitCode.value,
            unitCode: valueAndUnitCode.unitCode,
          },
        ]}
      >
        <VerticalAlignedChip
          label={parameterValue.parameter.name}
          onDelete={
            parameterValue.parameter.tmbId === 99999 || !enabled
              ? undefined
              : (): void => {
                  deleteChip(index);
                }
          }
          onClick={(): void => {
            showParameterValueDialog(chips as ParameterValue[], parameterValue as ParameterValue);
          }}
          variant="outlined"
          className={chipClassName(ParameterValueValidator.validate(parameterValue as ParameterValue).valid)}
        />
      </WhiteChipTooltip>
    );
  };

  const renderParameter = (parameterValue: ParameterValue, index: number): JSX.Element => {
    const valueAndUnitCode = extractValueAndUnitCode(parameterValue);
    return (
      <WhiteChipTooltip
        header={parameterValue.parameter.name}
        values={[
          {
            value: valueAndUnitCode.value,
            unitCode: valueAndUnitCode.unitCode,
          },
        ]}
      >
        <VerticalAlignedChip
          label={parameterValue.parameter.name}
          onDelete={
            enabled
              ? (): void => {
                  deleteChip(index);
                }
              : undefined
          }
          onClick={
            parameterValue.type === "NUMBER"
              ? (): void => {
                  showNumberParameterDialog(chips as ParameterValue[], parameterValue as NumberParameterValue);
                }
              : undefined
          }
          variant="outlined"
          className={chipClassName(
            parameterValue.type !== "NUMBER" || Boolean((parameterValue as NumberParameterValue).unitCode),
          )}
        />
      </WhiteChipTooltip>
    );
  };

  const addAction = (action: Action): void => {
    showParameterListDialog(
      action.name,
      action.parameters,
      [],
      // check length of parameters to prevent rendering includeLabel when only table actions present
      hasUncontrolledParameters && !!action.parameters.length,
      {
        fetchActionTemplates: () => fetchActionTemplates(action.tmbId),
        rowParameters: action.rowParameters,
      },
    )
      .then(({ values: parameterValues, rows }) => {
        addChip({
          action,
          parameterValues,
          rows: rows || [],
        });
      })
      .catch(() => {
        // do nothing on close
      });
  };

  const addParameter = (parameter: Parameter): void => {
    const index =
      parametersInAllTests?.findIndex((parameterValue) => parameterValue.parameter.tmbId === parameter.tmbId) || -1;

    const hintText =
      index >= 0 ? "By adding this parameter, the parameter will be removed from “Parameters in all Tests“" : undefined;

    showParameterListDialog(parameter.name, [parameter], [], hasUncontrolledParameters, { hintText })
      .then(({ values: parameterValues }) => {
        addChip(parameterValues[0]);
        if (index >= 0) {
          parametersInAllTests?.splice(index, 1);
          updateParentState(chips);
        }
      })
      .catch(() => {
        // do nothing on close
      });
  };

  const addMeasurementParameter = (parameter: Parameter): void => {
    const parameterValue = toParameterValue(parameter);
    if (parameterValue.type === "NUMBER") {
      const numberParameterValue = parameterValue as NumberParameterValue;
      const enumValue: EnumParameterValue = {
        type: "ENUM",
        value: numberParameterValue.unitCode,
        parameter: {
          tmbId: numberParameterValue.parameter.tmbId,
          values: numberParameterValue.parameter.unit.scalings.map((s) => s.code),
          name: numberParameterValue.parameter.name,
          type: "ENUM",
        },
      };
      showParameterListDialog(
        numberParameterValue.parameter.name,
        [enumValue.parameter],
        [enumValue],
        hasUncontrolledParameters,
        {
          unitType: numberParameterValue.parameter.unit.type,
        },
      )
        .then(({ values }) => {
          const enumResultValue = values[0] as EnumParameterValue;
          const unitCode = enumResultValue.value;

          if (values.length > 1) {
            updateUnit(values.slice(1), unitCode);
          }

          addChip({
            ...numberParameterValue,
            unitCode,
          } as NumberParameterValue);
        })
        .catch(() => {
          // do nothing on close
        });
    } else {
      addChip(parameterValue);
    }
  };

  // collect ids of entries to exclude from context menu
  const excludedIds = chips?.map((chip) => (chip as ParameterValue).parameter?.tmbId);
  conditions?.forEach((condition) =>
    condition.parameterValues.forEach((pameterValue) => excludedIds.push(pameterValue.parameter.tmbId)),
  );

  return (
    <Box className={classes.root}>
      <Box>
        {chips &&
          chips.map((e, i) => {
            const key = e.id || `${i}-index`;
            return (
              <Box key={key} className={classes.tableElement}>
                {section === "measurement" && renderParameter(e as ParameterValue, i)}
                {section === "condition" && renderParameterValue(e as ParameterValue, i)}
                {section !== "measurement" && section !== "condition" && renderActionValue(e as ActionValue, i)}
              </Box>
            );
          })}
      </Box>
      {enabled && (
        <Box paddingY={1}>
          {section === "measurement" && (
            <CustomDynamicContextMenu
              sortAlphanumericBy="name"
              excludedIds={chips.map((chip) => (chip as ParameterValue).parameter.tmbId)}
              fetch={(): Promise<TmbParameter[]> => {
                return tesaTmbApi.fetchParameters(section).then((response) => response.data.data);
              }}
              onMenuItemClick={(selectable): void => {
                addMeasurementParameter(mapParameter(selectable));
              }}
            />
          )}
          {section === "condition" && (
            <CustomDynamicContextMenu
              sortAlphanumericBy="name"
              excludedIds={excludedIds}
              fetch={(): Promise<TmbParameter[]> => {
                return tesaTmbApi.fetchParameters(section).then((response) => response.data.data);
              }}
              onMenuItemClick={(selectable): void => {
                addParameter(mapParameter(selectable));
              }}
            />
          )}
          {section !== "measurement" && section !== "condition" && (
            <CustomDynamicContextMenu
              sortAlphanumericBy="name"
              fetch={(): Promise<TmbAction[]> => {
                return tesaTmbApi.fetchActions(section, flowType).then((response) => response.data.data);
              }}
              onMenuItemClick={(selectable): void => {
                addAction(mapAction(selectable));
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default TestSeriesWhiteChipTableElement;
